/**
 * Rule Build Chat Component
 *
 * AI chat panel for interacting with the rule build assistant.
 * Handles message display, streaming, interrupts, and input.
 * Styled to match NoteChat design patterns with shared UI components.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EmptyState,
  InputFooter,
  MessageBubble,
  ScrollContainer,
} from '@components/ui/chat';
import { useMessageHover } from '@hooks/useMessageHover';
import {
  ArrowUpward as SendIcon,
  AutoAwesome as AiIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppSelector } from '@store/hooks';

import type { UseChatActionsResult } from '../hooks';
import type { RuleBuildChatState } from '../store';

import { ActivityBubble } from './ActivityBubble';
import { ConfirmationBanner } from './ConfirmationBanner';

export interface RuleBuildChatProps {
  taskId: string;
  chatActions: UseChatActionsResult;
}

export const RuleBuildChat: React.FC<RuleBuildChatProps> = ({
  taskId,
  chatActions,
}) => {
  const theme = useTheme();
  const { ruleBuild } = useEventTracking();
  const inputRef = useRef<HTMLInputElement>(null);

  // Local state
  const [inputValue, setInputValue] = useState('');
  const [isProcessingInterrupt, setIsProcessingInterrupt] = useState(false);

  // Message hover state for timestamp display
  const { hoveredMessageId, handleMouseEnter, handleMouseLeave } = useMessageHover();

  // Get chat state from Redux
  const chatState = useAppSelector(
    (state) => (state as unknown as { 'ruleBuild/chat'?: RuleBuildChatState })['ruleBuild/chat']
  );

  const messages = chatState?.messages || [];
  const activities = chatState?.activities || {};
  const streamingContent = chatState?.assistant?.streamingContent || '';
  const interrupt = chatState?.interrupt;
  const isStreaming = chatActions.isStreaming;
  const isThinking = chatActions.isThinking;
  const error = chatActions.error;

  // Check if there's an active interrupt
  const hasActiveInterrupt = interrupt?.isActive && interrupt?.eventType === 'CONFIRMATION';

  // Track interrupt shown
  const lastTrackedInterruptRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      interrupt?.isActive &&
      interrupt.interruptId &&
      lastTrackedInterruptRef.current !== interrupt.interruptId
    ) {
      lastTrackedInterruptRef.current = interrupt.interruptId;
      ruleBuild.chatInterruptShown(taskId, {
        interrupt_type: interrupt.eventType,
        message: interrupt.message?.substring(0, 100), // Truncate for privacy
      });
    }
  }, [interrupt?.isActive, interrupt?.interruptId, interrupt?.eventType, interrupt?.message, taskId, ruleBuild]);

  // Track chat errors
  useEffect(() => {
    if (error) {
      ruleBuild.chatError(taskId, {
        error_type: 'chat_error',
        error_message: error.substring(0, 100), // Truncate
      });
    }
  }, [error, taskId, ruleBuild]);

  // Handle send message
  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || !chatActions.isReady) return;

    // Track message sent
    ruleBuild.chatMessageSent(taskId, {
      message_length: trimmed.length,
      has_focused_draft: Boolean(chatState?.focusedDraftId),
    });

    chatActions.sendMessage(trimmed);
    setInputValue('');
  }, [inputValue, chatActions, taskId, chatState?.focusedDraftId, ruleBuild]);

  // Handle key press
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Handle stop streaming
  const handleStop = useCallback(() => {
    chatActions.cancelStream();
  }, [chatActions]);

  // Handle interrupt confirmation (user accepts)
  const handleInterruptConfirm = useCallback(() => {
    if (!interrupt?.interruptId) return;

    setIsProcessingInterrupt(true);
    
    // Track interrupt response
    ruleBuild.chatInterruptResponded(taskId, {
      interrupt_type: interrupt.eventType,
      response: 'confirmed',
    });
    
    // Resume with confirmed: true (ConfirmationResumePayload)
    chatActions.resumeInterrupt({ confirmed: true });

    // Reset processing state after a brief delay
    // The actual completion will clear the interrupt via Redux
    setTimeout(() => {
      setIsProcessingInterrupt(false);
    }, 500);
  }, [interrupt?.interruptId, interrupt?.eventType, chatActions, taskId, ruleBuild]);

  // Handle interrupt cancellation (user rejects)
  const handleInterruptCancel = useCallback(() => {
    if (!interrupt?.interruptId) return;

    setIsProcessingInterrupt(true);

    // Track interrupt response
    ruleBuild.chatInterruptResponded(taskId, {
      interrupt_type: interrupt.eventType,
      response: 'cancelled',
    });

    // Resume with confirmed: false (ConfirmationResumePayload)
    chatActions.resumeInterrupt({ confirmed: false });

    // Reset processing state
    setTimeout(() => {
      setIsProcessingInterrupt(false);
    }, 500);
  }, [interrupt?.interruptId, interrupt?.eventType, chatActions, taskId, ruleBuild]);

  // Get active activities
  const activeActivities = Object.values(activities);

  // Disable input when interrupt is active or processing
  const isInputDisabled = (!chatActions.isReady && !isStreaming) || hasActiveInterrupt;

  // Build display messages list with memoization
  const messageList = useMemo(() => {
    const items: React.ReactNode[] = [];

    // Regular messages
    messages.forEach((msg) => {
      items.push(
        <MessageBubble
          key={msg.id}
          id={msg.id}
          role={msg.role}
          content={msg.content}
          timestamp={msg.timestamp || new Date().toISOString()}
          isHovered={hoveredMessageId === msg.id}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    });

    // Activity indicators
    activeActivities.forEach((activity) => {
      items.push(
        <ActivityBubble
          key={`activity-${activity.messageId}`}
          activityType="THINKING"
          message={activity.message}
        />
      );
    });

    // Streaming message
    if (isStreaming && streamingContent) {
      items.push(
        <MessageBubble
          key="streaming-temp"
          id="streaming-temp"
          role="assistant"
          content={streamingContent}
          timestamp={new Date().toISOString()}
          isStreaming
          isHovered={false}
        />
      );
    }

    // Thinking indicator when no streaming content yet
    if (isThinking && !streamingContent) {
      items.push(
        <ActivityBubble
          key="thinking-indicator"
          activityType="THINKING"
          message="Thinking..."
        />
      );
    }

    return items;
  }, [messages, activeActivities, isStreaming, streamingContent, isThinking, hoveredMessageId, handleMouseEnter, handleMouseLeave]);

  // Check if we should show empty state
  const showEmptyState = messages.length === 0 && !isStreaming && !isThinking;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.grey[200]}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: `${theme.palette.primary.main}15`,
            color: theme.palette.primary.main,
          }}
        >
          <AiIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'textColors.darkText',
          }}
        >
          AI Assistant
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          onClose={chatActions.clearChatError}
          sx={{ m: 1.5, py: 0.5, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      {/* Messages - using ScrollContainer for auto-scroll */}
      <ScrollContainer
        className="rule-build-messages"
        emptyState={
          showEmptyState ? (
            <EmptyState
              title="AI Rule Assistant"
              description="Ask me to help create or refine rules."
              examples={[
                'Create a rule for Medicare billing requirements',
                'Add a condition to check for missing documentation',
                'Help me write a rule for prior authorization',
              ]}
            />
          ) : undefined
        }
      >
        {messageList}
      </ScrollContainer>

      {/* Confirmation Banner (for save confirmation, etc.) */}
      <ConfirmationBanner
        onConfirm={handleInterruptConfirm}
        onCancel={handleInterruptCancel}
        isProcessing={isProcessingInterrupt}
      />

      {/* Input Footer - using shared component for consistent styling */}
      <InputFooter elevation>
        <Stack
          direction="row"
          spacing={1}
          sx={{alignItems: "flex-end",  width: '100%' }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder={hasActiveInterrupt ? 'Respond to the prompt above...' : 'Ask the AI assistant...'}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isInputDisabled}
            multiline
            minRows={2}
            maxRows={4}
            sx={{
              flex: 1,
              minWidth: 0,
              width: 'auto',
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                borderRadius: 2,
                boxShadow: 'none',
                '& fieldset': {
                  borderColor: '#d5d3e0',
                  borderWidth: 1,
                },
                '&:hover fieldset': {
                  borderColor: '#ada1f3',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#836eed',
                  borderWidth: 2,
                },
                '&.Mui-disabled': {
                  bgcolor: '#faf9fc',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: 14,
                lineHeight: 1.5,
              },
            }}
          />
          {isStreaming ? (
            <IconButton
              onClick={handleStop}
              aria-label="Stop rule response"
              sx={{
                bgcolor: theme.palette.error.main,
                color: 'white',
                '&:hover': {
                  bgcolor: theme.palette.error.dark,
                },
                transition: 'all 0.2s ease',
                width: 40,
                height: 40,
              }}
            >
              <StopIcon sx={{ fontSize: 22 }} />
            </IconButton>
          ) : (
            <IconButton
              onClick={handleSend}
              aria-label="Send rule message"
              disabled={!inputValue.trim() || isInputDisabled}
              sx={{
                bgcolor: '#4D32D7',
                color: 'white',
                opacity: !inputValue.trim() || isInputDisabled ? 0.3 : 1,
                '&:hover': {
                  bgcolor: '#36229e',
                  opacity: !inputValue.trim() || isInputDisabled ? 0.3 : 1,
                },
                '&:disabled': {
                  bgcolor: '#4D32D7',
                  color: 'white',
                  opacity: 0.3,
                },
                transition: 'all 0.2s ease',
                width: 40,
                height: 40,
              }}
            >
              <SendIcon sx={{ fontSize: 22 }} />
            </IconButton>
          )}
        </Stack>
      </InputFooter>
    </Paper>
  );
};

export default RuleBuildChat;
